import { useEffect, useState } from "react";

import { getHighlighter, langFromPath, normalizeLang } from "@/lib/shiki";

/**
 * Syntax-highlighted code, rendered by Shiki with dual (light + dark) themes baked into one pass —
 * theme switching is pure CSS (see `.sp-code` rules in index.css), so no re-highlight on toggle.
 * Pass either a `path` (language inferred from the extension) or an explicit `lang` (fence info).
 */
export default function CodeViewer({ code, path, lang, plain }: { code: string; path?: string; lang?: string; plain?: boolean }) {
  const wanted = lang ?? (path ? langFromPath(path) : "text");
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getHighlighter()
      .then((hl) => {
        const l = normalizeLang(wanted, hl.getLoadedLanguages());
        const out = hl.codeToHtml(code, {
          lang: l,
          themes: { light: "one-light", dark: "one-dark-pro" },
          defaultColor: false, // emit both themes as CSS vars; index.css picks per data-theme
        });
        if (!cancelled) setHtml(out);
      })
      .catch(() => { if (!cancelled) setHtml(null); });
    return () => { cancelled = true; };
  }, [code, wanted]);

  // Fallback (still loading, or highlight failed): same panel/plain surface as the final render
  // (via .sp-code .shiki), just un-highlighted — so swapping in the highlighted HTML doesn't flash
  // the background. Tokens inherit the page text color until Shiki's per-theme colors arrive.
  if (html === null) {
    return (
      <div className={`sp-code${plain ? " sp-plain" : ""}`}>
        <pre className="shiki">
          <code>{code}</code>
        </pre>
      </div>
    );
  }
  return <div className={`sp-code${plain ? " sp-plain" : ""}`} dangerouslySetInnerHTML={{ __html: html }} />;
}
